/*
 * Словарь подписей ленты 006 (contracts/ui.md §2, §5; FR-032, FR-033). Один словарь: его берёт сервер для
 * проекции ленты (`api/src/modules/agent-thread/labels.ts`), а интерфейс — через
 * `web/src/components/assistant/texts.ts`.
 *
 * - Подпись шага — из подписей 005 (`MCP_TOOLS[].status`): «Смотрю правило «Обед»…» → «Посмотрел правило «Обед»».
 *   `{name}` — название правила по ключу из аргументов; названия нет — без кавычек.
 * - Служебные шаги (`status: null`, например `rules_about`) строк не дают.
 * - Свои шаги агента (чтение инструкций скилла) — общими словами, без имён инструментов.
 * Слов «тред», «ход», «контекст», «вмешательство», имён инструментов и JSON здесь нет.
 */
import { MCP_TOOLS, type McpToolDef } from '../mcp/tools';
import type { StepState } from './timeline';

const DEFS: ReadonlyMap<string, McpToolDef> = new Map(MCP_TOOLS.map((t) => [t.name, t as McpToolDef]));

/** Свои инструменты агентов (не 005): подпись «идёт» / «готово»; `null` — служебный, строки нет. */
const AGENT_TOOLS: Readonly<Record<string, { pending: string; done: string } | null>> = {
  Read: { pending: 'Читаю инструкции…', done: 'Прочитал инструкции' },
  Glob: { pending: 'Читаю инструкции…', done: 'Прочитал инструкции' },
  Grep: { pending: 'Читаю инструкции…', done: 'Прочитал инструкции' },
  LS: { pending: 'Читаю инструкции…', done: 'Прочитал инструкции' },
  Skill: { pending: 'Открываю инструкции…', done: 'Открыл инструкции' },
  read: { pending: 'Читаю инструкции…', done: 'Прочитал инструкции' },
  TodoWrite: null,
  ToolSearch: null,
};
const OTHER = { pending: 'Выполняю действие…', done: 'Выполнил действие' };

const trimDots = (s: string) => s.replace(/…$/u, '');
const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/** Подписи по состоянию шага; `{name}` уже подставлено. */
function byState(pending: string, done: string, state: StepState): string {
  switch (state) {
    case 'running':
      return pending;
    case 'done':
      return done;
    case 'error':
      return `Не получилось: ${lowerFirst(trimDots(pending))}`;
    case 'denied':
      return `Запрещено: ${lowerFirst(trimDots(pending))}`;
    case 'stopped':
      return `Остановлено: ${lowerFirst(trimDots(pending))}`;
  }
}

/**
 * Подпись шага. `tool` — имя инструмента без `mcp__efes__`; `ruleTitle` — название правила по ключу `group`.
 * `null` — служебный шаг (FR-032).
 */
export function stepLabel(
  tool: string,
  args: unknown,
  state: StepState,
  ruleTitle: (group: string) => string | null = () => null,
): string | null {
  const def = DEFS.get(tool);
  const own = def ? undefined : tool in AGENT_TOOLS ? AGENT_TOOLS[tool] : OTHER;
  if (def?.status === null || own === null) return null;
  const st = def?.status ?? own ?? OTHER;
  const group = (args as { group?: unknown } | undefined)?.group;
  const title = typeof group === 'string' ? ruleTitle(group) : null;
  const fill = (s: string) => (title ? s.replace('{name}', title) : s.replace(/ «\{name\}»/u, ''));
  return byState(fill(st.pending), fill(st.done), state);
}

/** Есть ли у инструмента 005 подпись или он служебный (тест словаря, contracts/ui.md §5). */
export function hasStepLabel(tool: string): boolean {
  const def = DEFS.get(tool);
  return def !== undefined && (def.status === null || def.status.pending.length > 0);
}

const RUN_ERROR_TEXT: Readonly<Record<string, string>> = {
  auth_required: 'вход в агента не выполнен или истёк',
  rate_limited: 'исчерпан лимит модели',
  overloaded: 'модель перегружена',
  context_full: 'память разговора заполнена',
  connection_lost: 'пропала связь с компьютером',
  turn_not_started: 'агент не начал работу за 2 минуты',
  agent_failed: 'агент завершился с ошибкой',
  computer_disabled: 'компьютер отключён от табеля',
};

/** Время «чч:мм» по часовому поясу объекта (`tz_offset` из config/objects.yaml). */
export function clock(iso: string, tzOffset = '+00:00'): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const sign = tzOffset.startsWith('-') ? -1 : 1;
  const [h, m] = tzOffset.slice(1).split(':').map(Number);
  const local = new Date(ms + sign * ((h ?? 0) * 60 + (m ?? 0)) * 60_000);
  return `${String(local.getUTCHours()).padStart(2, '0')}:${String(local.getUTCMinutes()).padStart(2, '0')}`;
}

/** Тексты системных строк ленты (contracts/ui.md §3). */
export function systemTexts(tzOffset: string) {
  return {
    stopped: 'Агент остановлен.',
    stoppedText: 'Это не ошибка.',
    error: (code: string, _message: string) => ({
      title: `Агент не ответил: ${RUN_ERROR_TEXT[code] ?? RUN_ERROR_TEXT.agent_failed}.`,
      text: `Разговор сохранён. Код ошибки: ${code}`,
    }),
    modelChanged: (from: string, to: string) => `Модель сменилась: ${from} → ${to}`,
    newSession: 'Начат новый разговор с агентом: прежний не продолжить',
    offline: (since: string) => `Компьютер не в сети с ${clock(since, tzOffset)}`,
    online: 'Компьютер снова в сети',
    disabled: 'Компьютер отключён от табеля',
  };
}

export { RUN_ERROR_TEXT };
